import type {Thread} from '@github-ui/diff-lines/conversation-types'
import type {IssueComment} from '../page-data/payloads/issue-comments'
import {getFirstComment} from '../page-data/payloads/thread-previews'

export interface AuthorFilterItem {
  login: string
  avatarUrl: string
}

/**
 * Sorts thread previews by diff path order and line number.
 * Threads are first ordered by their position in the diff (using diffPathOrderMap),
 * then by line number within the same file.
 * Threads without a matching diff entry are sorted to the end.
 */
export function sortThreadPreviewsByDiffOrder(threads: Thread[], diffPathOrderMap: Map<string, number>): Thread[] {
  if (diffPathOrderMap.size === 0) return threads

  return [...threads].sort((a, b) => {
    const pathA = a.path ?? a.positioning?.path
    const pathB = b.path ?? b.positioning?.path
    const orderA = pathA !== undefined ? diffPathOrderMap.get(pathA) : undefined
    const orderB = pathB !== undefined ? diffPathOrderMap.get(pathB) : undefined

    // Threads without a matching diff entry sort to the end
    if (orderA === undefined && orderB === undefined) return 0
    if (orderA === undefined) return 1
    if (orderB === undefined) return -1
    if (orderA !== orderB) return orderA - orderB

    // Same file: sort by line number
    const lineA = a.subjectType === 'FILE' ? 0 : (a.positioning?.endLine ?? a.line ?? a.subject?.endLine)
    const lineB = b.subjectType === 'FILE' ? 0 : (b.positioning?.endLine ?? b.line ?? b.subject?.endLine)
    return (lineA ?? Infinity) - (lineB ?? Infinity)
  })
}

/**
 * Builds a list of unique authors from thread previews.
 * Copilot authors are placed at the top of the list.
 */
export function buildThreadPreviewsAuthorList(threadPreviews: Thread[]): AuthorFilterItem[] {
  const authors: AuthorFilterItem[] = []

  for (const threadPreview of threadPreviews) {
    const firstComment = getFirstComment(threadPreview)
    if (firstComment?.author) {
      addUniqueAuthor(authors, firstComment.author)
    }
  }

  return authors
}

/**
 * Builds a list of unique authors from issue comments.
 * Copilot authors are placed at the top of the list.
 */
export function buildIssueCommentsAuthorList(comments: IssueComment[]): AuthorFilterItem[] {
  const authors: AuthorFilterItem[] = []

  for (const comment of comments) {
    if (comment.authorLogin) {
      addUniqueAuthor(authors, {login: comment.authorLogin, avatarUrl: comment.authorAvatarUrl ?? ''})
    }
  }

  return authors
}

/**
 * Adds an author to the authors list if not already present.
 * Copilot authors are placed at the top of the list.
 */
function addUniqueAuthor(authors: AuthorFilterItem[], author: AuthorFilterItem): void {
  if (!authors.some(a => a.login === author.login)) {
    if (author.login === 'Copilot') {
      authors.unshift(author)
    } else {
      authors.push(author)
    }
  }
}
